/**
 * wim-gsap-shim.js
 * Reemplazo mínimo de GSAP 1.20.3 (TweenLite/TimelineLite + eases Cubic/Quad),
 * respaldado por un ticker rAF propio (Fase 3c del ADR de rendimiento).
 *
 * theme.min.js (bundle nativo, NO editable) usa un subconjunto acotado de GSAP:
 *   - `new TimelineLite({delay, onComplete})`
 *   - `.fromTo/.to/.staggerFromTo/.addLabel/.clear/.kill/.reverse/.timeScale/.eventCallback`
 *   - `TweenLite.fromTo(target, dur, from, to)` (one-shot estático)
 *   - eases `Cubic.easeOut/easeIn/easeInOut`, `Quad.easeOut` (default ≈ Power1.easeOut)
 *   - vars: autoAlpha (opacity+visibility), opacity, x, y (px/%), scale, height, display, transition
 * Solo se usan reveals de fade+slide y algún cross-fade; nunca se leen props de la
 * instancia (.progress/.time/.duration) ni se usa .play/.pause. Ver spec en el ADR.
 *
 * Debe cargarse (defer) ANTES de theme.min.js y sustituye a GSAP, que se retira de
 * libs.min.js. NO editar la lógica sin re-verificar los ~8 componentes en navegador.
 */
(() => {
  'use strict';
  if (window.__wimGsapShim) return;
  window.__wimGsapShim = true;

  // ── Eases (funciones puras t∈[0,1]) ─────────────────────────────────────────
  const E = {
    linear: (t) => t,
    quadIn: (t) => t * t,
    quadOut: (t) => 1 - (1 - t) * (1 - t),
    quadInOut: (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2),
    cubicIn: (t) => t * t * t,
    cubicOut: (t) => 1 - Math.pow(1 - t, 3),
    cubicInOut: (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)
  };
  const DEFAULT_EASE = E.quadOut; // GSAP default Power1.easeOut
  const Cubic = { easeIn: E.cubicIn, easeOut: E.cubicOut, easeInOut: E.cubicInOut };
  const Quad = { easeIn: E.quadIn, easeOut: E.quadOut, easeInOut: E.quadInOut };
  const Linear = { easeNone: E.linear, easeIn: E.linear, easeOut: E.linear, easeInOut: E.linear };
  window.Cubic = window.Cubic || Cubic;
  window.Quad = window.Quad || Quad;
  window.Linear = window.Linear || Linear;
  const resolveEase = (e) => (typeof e === 'function' ? e : DEFAULT_EASE);

  // ── Ticker rAF compartido ────────────────────────────────────────────────────
  const active = new Set();
  let rafId = null;
  let lastT = 0;
  const raf = window.requestAnimationFrame
    ? window.requestAnimationFrame.bind(window)
    : (fn) => setTimeout(() => fn(Date.now()), 16);
  const tick = (now) => {
    const dt = Math.min((now - lastT) / 1000, 0.1); // cap salto (tab refocus)
    lastT = now;
    active.forEach((tl) => tl._advance(dt));
    rafId = active.size ? raf(tick) : null;
  };
  const start = (tl) => {
    if (active.has(tl)) return;
    active.add(tl);
    if (rafId == null) {
      lastT = (window.performance && window.performance.now && window.performance.now()) || 0;
      rafId = raf(tick);
    }
  };

  // ── Normalización de vars ────────────────────────────────────────────────────
  const KEYS = ['autoAlpha', 'opacity', 'x', 'y', 'scale', 'height', 'display', 'transition'];
  const parseLen = (v) => {
    if (typeof v === 'number') return { v: v, unit: 'px' };
    const m = /^(-?[\d.]+)(px|%|em|rem|vw|vh)?$/.exec(String(v));
    return m ? { v: parseFloat(m[1]), unit: m[2] || 'px' } : { v: 0, unit: 'px' };
  };
  const normalize = (vars) => {
    const out = {};
    if (!vars) return out;
    KEYS.forEach((k) => {
      if (!(k in vars)) return;
      if (k === 'x' || k === 'y' || k === 'height') out[k] = parseLen(vars[k]);
      else if (k === 'opacity' || k === 'autoAlpha' || k === 'scale') out[k] = parseFloat(vars[k]);
      else out[k] = vars[k]; // display, transition (discretas)
    });
    return out;
  };
  // Captura el estado actual del elemento para los props de `to`/`from` implícitos.
  const captureFrom = (el, toVars) => {
    const cs = window.getComputedStyle(el);
    const out = {};
    if ('opacity' in toVars) out.opacity = parseFloat(cs.opacity);
    if ('autoAlpha' in toVars) out.autoAlpha = parseFloat(cs.opacity);
    if ('height' in toVars) out.height = { v: el.offsetHeight, unit: 'px' };
    if ('x' in toVars) out.x = { v: 0, unit: 'px' };
    if ('y' in toVars) out.y = parseLen(0); // identidad; unit lo fija el `to`
    if ('scale' in toVars) out.scale = 1;
    return out;
  };

  // ── Aplicar props interpolados a un elemento ─────────────────────────────────
  const lerp = (a, b, e) => a + (b - a) * e;
  const numOr = (x, d) => (x == null ? d : x);
  const lenV = (o) => (o ? o.v : 0);
  const lenU = (a, b) => (a && a.unit) || (b && b.unit) || 'px';

  const applyContinuous = (el, from, to, e) => {
    if ('autoAlpha' in from || 'autoAlpha' in to) {
      const o = lerp(numOr(from.autoAlpha, 1), numOr(to.autoAlpha, 1), e);
      el.style.opacity = o;
      el.style.visibility = o <= 0.001 ? 'hidden' : 'visible';
    } else if ('opacity' in from || 'opacity' in to) {
      el.style.opacity = lerp(numOr(from.opacity, 1), numOr(to.opacity, 1), e);
    }
    const hasX = 'x' in from || 'x' in to;
    const hasY = 'y' in from || 'y' in to;
    const hasS = 'scale' in from || 'scale' in to;
    if (hasX || hasY || hasS) {
      const xv = lerp(lenV(from.x), lenV(to.x), e);
      const yv = lerp(lenV(from.y), lenV(to.y), e);
      const sv = lerp(numOr(from.scale, 1), numOr(to.scale, 1), e);
      let tf = '';
      if (hasX || hasY) tf += 'translate3d(' + xv + lenU(to.x, from.x) + ',' + yv + lenU(to.y, from.y) + ',0)';
      if (hasS) tf += ' scale(' + sv + ')';
      el.style.transform = tf.trim();
    }
    if ('height' in from || 'height' in to) {
      el.style.height = lerp(lenV(from.height), lenV(to.height), e) + lenU(to.height, from.height);
    }
  };
  // Props discretas (display/transition): from al arrancar, to al terminar.
  const applyDiscrete = (el, tw, p) => {
    const { from, to } = tw;
    if (!tw._started && p > 0) {
      tw._started = true;
      // Si el tween revela (opacity sube), mostrar ya con el display de destino.
      const fadingIn = numOr(to.autoAlpha, numOr(to.opacity, 1)) > numOr(from.autoAlpha, numOr(from.opacity, 0));
      if (fadingIn && to.display) el.style.display = to.display;
      else if ('display' in from) el.style.display = from.display;
      if ('transition' in from) el.style.transition = from.transition;
    }
    if (!tw._ended && p >= 1) {
      tw._ended = true;
      if ('display' in to) el.style.display = to.display;
      if ('transition' in to) el.style.transition = to.transition;
    }
  };

  // ── Timeline ─────────────────────────────────────────────────────────────────
  class Timeline {
    constructor(config) {
      config = config || {};
      this._tweens = [];
      this._labels = {};
      this._time = 0;
      this._duration = 0;
      this._dir = 1;
      this._ts = 1;
      this._delay = config.delay || 0;
      this._onComplete = config.onComplete || null;
      this._onReverseComplete = null;
      this._done = false;
      start(this); // auto-play (los tweens se añaden síncronamente tras construir)
    }

    _resolve(pos) {
      if (pos == null) return this._duration;
      if (typeof pos === 'number') return pos;
      const rel = /^(\w+)?\s*([+-]=)([\d.]+)$/.exec(pos);
      if (rel) {
        const base = rel[1] ? this._labels[rel[1]] || 0 : this._duration;
        return rel[2] === '-=' ? base - parseFloat(rel[3]) : base + parseFloat(rel[3]);
      }
      return this._labels[pos] != null ? this._labels[pos] : this._duration;
    }

    _push(el, dur, from, to, extraDelay, pos, immediate) {
      const toDelay = (to && to.delay) || 0;
      const st = this._resolve(pos) + toDelay + (extraDelay || 0);
      const tw = {
        el: el,
        start: st,
        dur: dur || 0,
        ease: resolveEase(to && to.ease),
        from: from,
        to: to.__norm ? to : normalize(to),
        _started: false,
        _ended: false
      };
      this._tweens.push(tw);
      this._duration = Math.max(this._duration, st + tw.dur);
      // immediateRender (como GSAP fromTo/from/staggerFromTo): aplica el estado `from`
      // de inmediato para evitar el flash de contenido visible antes del primer frame.
      if (immediate) applyContinuous(el, from, tw.to, 0);
      // Re-activar si estaba idle (reveals que añaden tweens tras terminar).
      if (!active.has(this) && this._dir > 0) {
        this._done = false;
        start(this);
      }
      return tw;
    }

    fromTo(target, dur, fromVars, toVars, pos) {
      const els = toArray(target);
      const nf = normalize(fromVars);
      const nt = normalize(toVars);
      nt.delay = (toVars && toVars.delay) || 0;
      nt.ease = toVars && toVars.ease;
      nt.__norm = true;
      els.forEach((el) => this._push(el, dur, Object.assign({}, nf), Object.assign({}, nt), 0, pos, true));
      return this;
    }

    to(target, dur, toVars, pos) {
      const els = toArray(target);
      els.forEach((el) => {
        const from = captureFrom(el, toVars || {});
        const nt = normalize(toVars);
        nt.delay = (toVars && toVars.delay) || 0;
        nt.ease = toVars && toVars.ease;
        nt.__norm = true;
        this._push(el, dur, from, nt, 0, pos);
      });
      return this;
    }

    from(target, dur, fromVars, pos) {
      const els = toArray(target);
      els.forEach((el) => {
        const to = captureFrom(el, fromVars || {});
        this._push(el, dur, normalize(fromVars), Object.assign({ __norm: true }, to), 0, pos, true);
      });
      return this;
    }

    staggerFromTo(targets, dur, fromVars, toVars, stagger, pos) {
      const els = toArray(targets);
      const nf = normalize(fromVars);
      const base = this._resolve(pos);
      els.forEach((el, i) => {
        const nt = normalize(toVars);
        nt.delay = (toVars && toVars.delay) || 0;
        nt.ease = toVars && toVars.ease;
        nt.__norm = true;
        // posición absoluta = base + i*stagger (usamos pos numérica para respetarla)
        this._push(el, dur, Object.assign({}, nf), nt, i * (stagger || 0), base, true);
      });
      return this;
    }

    addLabel(name, pos) {
      this._labels[name] = this._resolve(pos);
      return this;
    }

    eventCallback(type, fn) {
      if (type === 'onReverseComplete') this._onReverseComplete = fn;
      else if (type === 'onComplete') this._onComplete = fn;
      return this;
    }

    clear() {
      this._tweens = [];
      this._labels = {};
      this._time = 0;
      this._duration = 0;
      this._dir = 1;
      this._ts = 1;
      this._done = false;
      return this;
    }

    reverse() {
      this._dir = -1;
      this._done = false;
      // des-asentar los tweens para que se re-rendericen hacia atrás.
      for (let i = 0; i < this._tweens.length; i++) {
        const tw = this._tweens[i];
        tw._settled = false;
        tw._started = false;
        tw._ended = false;
      }
      start(this);
      return this;
    }

    timeScale(n) {
      this._ts = n || 1;
      return this;
    }

    kill() {
      active.delete(this);
      this._tweens = [];
      return this;
    }

    _advance(dt) {
      if (this._delay > 0) {
        this._delay -= dt;
        this._render();
        if (this._delay > 0) return;
        dt = -this._delay;
        this._delay = 0;
      }
      this._time += dt * this._ts * this._dir;
      let finished = false;
      let reversed = false;
      if (this._dir > 0 && this._time >= this._duration) {
        this._time = this._duration;
        finished = true;
      } else if (this._dir < 0 && this._time <= 0) {
        this._time = 0;
        reversed = true;
      }
      this._render();
      if (finished || reversed) {
        active.delete(this);
        this._done = true;
        // No disparar onComplete en una timeline vacía (aún sin tweens): evita
        // que un reveal recién creado (que se puebla luego) lo dispare en frame 1.
        if (finished && this._onComplete && this._tweens.length) this._onComplete();
        if (reversed && this._onReverseComplete) this._onReverseComplete();
      }
    }

    _render() {
      const fwd = this._dir > 0;
      for (let i = 0; i < this._tweens.length; i++) {
        const tw = this._tweens[i];
        if (tw._settled) continue; // ya en su estado final: no re-renderizar cada frame
        const local = this._time - tw.start;
        let p;
        if (tw.dur <= 0) p = local >= 0 ? 1 : 0;
        else p = local <= 0 ? 0 : local >= tw.dur ? 1 : local / tw.dur;
        applyContinuous(tw.el, tw.from, tw.to, tw.ease(p));
        applyDiscrete(tw.el, tw, p);
        // Asentar el tween al completar en forward: deja de re-aplicarse cada frame y,
        // si acaba en transform identidad, se limpia (evita capa GPU permanente por elemento).
        if (fwd && p >= 1) {
          tw._settled = true;
          const to = tw.to;
          const hasTf =
            'x' in to || 'y' in to || 'scale' in to ||
            'x' in tw.from || 'y' in tw.from || 'scale' in tw.from;
          if (hasTf && lenV(to.x) === 0 && lenV(to.y) === 0 && numOr(to.scale, 1) === 1) {
            tw.el.style.transform = '';
          }
        }
      }
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────
  function toArray(t) {
    if (!t) return [];
    if (t.nodeType === 1) return [t];
    if (typeof t === 'string') return Array.prototype.slice.call(document.querySelectorAll(t));
    if (t.length != null) return Array.prototype.slice.call(t);
    return [t];
  }

  // ── TweenLite estático (one-shot) ────────────────────────────────────────────
  const TweenLite = {
    fromTo: (target, dur, fromVars, toVars) => new Timeline().fromTo(target, dur, fromVars, toVars),
    to: (target, dur, toVars) => new Timeline().to(target, dur, toVars),
    from: (target, dur, fromVars) => new Timeline().from(target, dur, fromVars),
    set: (target, vars) => new Timeline().fromTo(target, 0, vars, vars)
  };

  // ── Exponer globals (alias Max → Lite) ───────────────────────────────────────
  window.TimelineLite = Timeline;
  window.TimelineMax = Timeline;
  window.TweenLite = TweenLite;
  window.TweenMax = TweenLite;
})();
